export function pixelateImageData(
  imageData: Uint8ClampedArray,
  width: number,
  height: number,
  pixelSize: number,
  palette: ReadonlyArray<{ r: number; g: number; b: number }>,
  options?: { dither?: boolean; mergeRareColors?: boolean },
): { pixels: number[][]; width: number; height: number } {
  function rgbToLab(
    r: number,
    g: number,
    b: number,
  ): { l: number; a: number; b: number } {
    let rn = r / 255;
    let gn = g / 255;
    let bn = b / 255;
    rn = rn > 0.04045 ? Math.pow((rn + 0.055) / 1.055, 2.4) : rn / 12.92;
    gn = gn > 0.04045 ? Math.pow((gn + 0.055) / 1.055, 2.4) : gn / 12.92;
    bn = bn > 0.04045 ? Math.pow((bn + 0.055) / 1.055, 2.4) : bn / 12.92;
    const x = rn * 0.4124564 + gn * 0.3575761 + bn * 0.1804375;
    const y = rn * 0.2126729 + gn * 0.7151522 + bn * 0.072175;
    const z = rn * 0.0193339 + gn * 0.119192 + bn * 0.9503041;
    const xn = x / 0.95047;
    const yn = y / 1.0;
    const zn = z / 1.08883;
    const fx = xn > 0.008856 ? Math.pow(xn, 1 / 3) : 7.787 * xn + 16 / 116;
    const fy = yn > 0.008856 ? Math.pow(yn, 1 / 3) : 7.787 * yn + 16 / 116;
    const fz = zn > 0.008856 ? Math.pow(zn, 1 / 3) : 7.787 * zn + 16 / 116;
    return {
      l: 116 * fy - 16,
      a: 500 * (fx - fy),
      b: 200 * (fy - fz),
    };
  }

  function labChroma(lab: { a: number; b: number }): number {
    return Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  }

  function labHueDeg(lab: { a: number; b: number }): number {
    return (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
  }

  function hueDiff(h1: number, h2: number): number {
    let d = Math.abs(h1 - h2);
    return d > 180 ? 360 - d : d;
  }

  const CHROMA_SATURATED = 25;
  const CHROMA_NEUTRAL = 12;
  const HUE_PENALTY_FACTOR = 0.5;

  function findClosestColor(
    r: number,
    g: number,
    b: number,
  ): number {
    let minDistance = Infinity;
    let closestIndex = 0;
    const inputLab = rgbToLab(r, g, b);
    const inputChroma = labChroma(inputLab);
    const inputHue = labHueDeg(inputLab);

    for (let index = 0; index < palette.length; index++) {
      const color = palette[index];
      const colorLab = rgbToLab(color.r, color.g, color.b);
      const deltaE = Math.sqrt(
        Math.pow(inputLab.l - colorLab.l, 2) +
          Math.pow(inputLab.a - colorLab.a, 2) +
          Math.pow(inputLab.b - colorLab.b, 2),
      );
      const paletteChroma = labChroma(colorLab);
      let weightedDistance = deltaE;
      if (inputChroma > CHROMA_SATURATED && paletteChroma < CHROMA_NEUTRAL) {
        weightedDistance += 60;
      } else if (
        inputChroma > CHROMA_SATURATED &&
        paletteChroma >= CHROMA_NEUTRAL
      ) {
        weightedDistance +=
          hueDiff(inputHue, labHueDeg(colorLab)) * HUE_PENALTY_FACTOR;
      }
      if (weightedDistance < minDistance) {
        minDistance = weightedDistance;
        closestIndex = index;
      }
    }
    return closestIndex;
  }

  const gridW = Math.ceil(width / pixelSize);
  const gridH = Math.ceil(height / pixelSize);

  // Pass 1: per-cell average colors. Kept as a mutable grid so the
  // dithering pass can diffuse quantization error across neighbors.
  const avg = new Float64Array(gridW * gridH * 3);
  for (let y = 0; y < gridH; y++) {
    for (let x = 0; x < gridW; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let py = 0; py < pixelSize; py++) {
        for (let px = 0; px < pixelSize; px++) {
          const ix = x * pixelSize + px;
          const iy = y * pixelSize + py;
          if (ix < width && iy < height) {
            const i = (iy * width + ix) * 4;
            r += imageData[i];
            g += imageData[i + 1];
            b += imageData[i + 2];
            n++;
          }
        }
      }
      const o = (y * gridW + x) * 3;
      if (n > 0) {
        avg[o] = Math.round(r / n);
        avg[o + 1] = Math.round(g / n);
        avg[o + 2] = Math.round(b / n);
      }
    }
  }

  // Pass 2: match each cell to the palette. With `dither`, the RGB gap
  // between the cell's average and the matched color is pushed onto
  // neighboring cells (Floyd–Steinberg, brick-cell level).
  const dither = options?.dither === true;
  const pixels: number[][] = [];

  for (let y = 0; y < gridH; y++) {
    const row: number[] = [];
    for (let x = 0; x < gridW; x++) {
      const o = (y * gridW + x) * 3;
      const idx = findClosestColor(avg[o], avg[o + 1], avg[o + 2]);
      row.push(idx);

      if (dither) {
        const er = avg[o] - palette[idx].r;
        const eg = avg[o + 1] - palette[idx].g;
        const eb = avg[o + 2] - palette[idx].b;
        const spread = (nx: number, ny: number, w: number) => {
          if (nx < 0 || nx >= gridW || ny >= gridH) return;
          const t = (ny * gridW + nx) * 3;
          avg[t] += er * w;
          avg[t + 1] += eg * w;
          avg[t + 2] += eb * w;
        };
        spread(x + 1, y, 7 / 16);
        spread(x - 1, y + 1, 3 / 16);
        spread(x, y + 1, 5 / 16);
        spread(x + 1, y + 1, 1 / 16);
      }
    }
    pixels.push(row);
  }

  // Pass 3: merge rare colors. A palette color covering < 2% of the mosaic
  // is remapped to its nearest surviving color (plain CIELAB ΔE) — cleans
  // up anti-aliasing speckle and shrinks the parts list. The most common
  // color is never rare, so a merge target always exists.
  if (options?.mergeRareColors === true) {
    const counts = new Map<number, number>();
    let total = 0;
    for (const row of pixels) {
      for (const idx of row) {
        counts.set(idx, (counts.get(idx) ?? 0) + 1);
        total++;
      }
    }
    let mostCommon = -1;
    let mostCount = 0;
    for (const [idx, n] of counts) {
      if (n > mostCount) {
        mostCount = n;
        mostCommon = idx;
      }
    }
    const rare = new Set(
      [...counts.keys()].filter(
        (idx) => idx !== mostCommon && counts.get(idx)! / total < 0.02,
      ),
    );
    if (rare.size > 0) {
      const labs = palette.map((c) => rgbToLab(c.r, c.g, c.b));
      const remap = new Map<number, number>();
      for (const r of rare) {
        let best = mostCommon;
        let bestD = Infinity;
        for (const idx of counts.keys()) {
          if (rare.has(idx)) continue;
          const d =
            (labs[r].l - labs[idx].l) ** 2 +
            (labs[r].a - labs[idx].a) ** 2 +
            (labs[r].b - labs[idx].b) ** 2;
          if (d < bestD) {
            bestD = d;
            best = idx;
          }
        }
        remap.set(r, best);
      }
      for (let y = 0; y < gridH; y++) {
        for (let x = 0; x < gridW; x++) {
          const mapped = remap.get(pixels[y][x]);
          if (mapped !== undefined) pixels[y][x] = mapped;
        }
      }
    }
  }

  return { pixels, width: gridW, height: gridH };
}
